/**
 * usePendingCount Hook
 *
 * Fetches pending verification count for alerts using URQL query.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const PendingCountDocument = `
  query PendingCount {
    adminDashboardStats {
      pendingVerification
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseAdminPendingCountOptions {
  pause?: boolean;
}

interface UseAdminPendingCountResult {
  pendingVerification: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch pending verification count for alerts.
 */
export function usePendingCount(
  options: UseAdminPendingCountOptions = {}
): UseAdminPendingCountResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    adminDashboardStats: { pendingVerification: number };
  }>({
    query: PendingCountDocument,
    pause,
  });

  return {
    pendingVerification: result.data?.adminDashboardStats?.pendingVerification ?? 0,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
